import { useState } from 'react';
import { CheckCircle, XCircle, Search, Calendar, User, Clock, AlertCircle } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Card, CardContent } from '@/components/ui/Card';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { useToast } from '@/components/ui/Toast';
import { useLeaveApplications, useApproveLeave } from '@/hooks/useLeave';
import { LEAVE_TYPES } from '@/types';
import { formatDate } from '@/lib/utils';

export function LeaveApprovalPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage] = useState(1);
  const [pageSize] = useState(20);
  const [approvalNotes, setApprovalNotes] = useState<Record<string, string>>({});
  
  const { toast } = useToast();
  const { data: applicationsData } = useLeaveApplications({
    status: 'pending',
    search: searchQuery,
    page: currentPage,
    limit: pageSize,
  });
  
  const approveLeave = useApproveLeave();

  const applications = applicationsData?.data || [];
  const totalItems = applicationsData?.meta?.total || 0;

  const handleApprove = async (id: string, approved: boolean) => {
    try {
      await approveLeave.mutateAsync({ 
        id, 
        approved, 
        notes: approvalNotes[id] 
      });
      toast({
        title: 'Success',
        description: `Leave ${approved ? 'approved' : 'rejected'} successfully`,
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to process leave application',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Leave Approvals"
        description="Review and approve pending leave applications"
      />

      {/* Summary Card */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-warning/10 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-warning" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Approvals</p>
                <p className="text-2xl font-bold">{totalItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Clock className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Hours</p>
                <p className="text-2xl font-bold">
                  {applications.reduce((sum, a) => sum + a.totalHours, 0).toFixed(1)} hrs
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-success/10 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">This Week</p>
                <p className="text-2xl font-bold">
                  {applications.filter(a => {
                    const start = new Date(a.startDate);
                    const now = new Date();
                    const diffDays = Math.floor((start.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                    return diffDays >= 0 && diffDays <= 7;
                  }).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search applications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Applications List */}
      {applications.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-12 w-12 text-success mx-auto mb-4" />
            <h3 className="text-lg font-medium">All Caught Up!</h3>
            <p className="text-muted-foreground">
              There are no pending leave applications to review.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {applications.map((application) => (
            <Card key={application.id} className="overflow-hidden">
              <CardContent className="p-0">
                <div className="p-4 border-b bg-muted/30">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <User className="h-5 w-5 text-muted-foreground" />
                      <span className="font-medium text-lg">{application.employeeName}</span>
                      <Badge variant="outline">
                        {LEAVE_TYPES.find(t => t.value === application.leaveType)?.label || application.leaveType}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {formatDate(application.startDate)} - {formatDate(application.endDate)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {application.totalHours} hrs
                      </span>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="mb-4">
                    <p className="text-sm text-muted-foreground mb-1">Reason:</p>
                    <p className="text-sm">{application.reason || 'No reason provided'}</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`notes-${application.id}`}>Approval Notes (Optional)</Label>
                    <Textarea
                      id={`notes-${application.id}`}
                      placeholder="Add notes about this approval decision..."
                      value={approvalNotes[application.id] || ''}
                      onChange={(e) => setApprovalNotes(prev => ({ ...prev, [application.id]: e.target.value }))}
                      rows={2}
                    />
                  </div>
                  <div className="flex justify-end gap-2 mt-4">
                    <Button 
                      variant="outline"
                      className="text-destructive hover:bg-destructive/10"
                      onClick={() => handleApprove(application.id, false)}
                      disabled={approveLeave.isPending}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                    <Button 
                      className="text-success hover:bg-success/10"
                      onClick={() => handleApprove(application.id, true)}
                      disabled={approveLeave.isPending}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
